import React from 'react';
import { Buildings, Briefcase, ShareNetwork, Users, TreeStructure } from '@phosphor-icons/react';
import type { OrgPathItem } from '../../types/api';

interface OrgHierarchyPathDisplayProps {
  path: OrgPathItem[] | null | undefined;
  className?: string;
}

const LEVEL_COLORS: Record<string, { icon: string; bg: string; border: string; line: string }> = {
  'Organization': { icon: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-300', line: '#818cf8' },
  'Business Unit': { icon: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-300', line: '#60a5fa' },
  'Division': { icon: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-300', line: '#22d3ee' },
  'Department': { icon: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-300', line: '#34d399' },
  'Team': { icon: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-300', line: '#a78bfa' },
};

const DEFAULT_COLORS = { icon: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-300', line: '#94a3b8' };
const getColors = (level: string) => LEVEL_COLORS[level] || DEFAULT_COLORS;

const getLevelIcon = (level: string, colorClass: string): React.ReactNode => {
  const iconProps = { className: `w-4 h-4 ${colorClass}`, weight: 'fill' as const };
  switch (level) {
    case 'Organization': return <Buildings {...iconProps} />;
    case 'Business Unit': return <Briefcase {...iconProps} />;
    case 'Division': return <ShareNetwork {...iconProps} />;
    case 'Department':
    case 'Team': return <Users {...iconProps} />;
    default: return <TreeStructure {...iconProps} />;
  }
};

export const OrgHierarchyPathDisplay: React.FC<OrgHierarchyPathDisplayProps> = ({ path, className = '' }) => {
  if (!path || path.length === 0) {
    return <div className="text-sm text-slate-500">Not Assigned</div>;
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`}>
      {path.map((item, index) => {
        const colors = getColors(item.level);
        const nextColors = index < path.length - 1 ? getColors(path[index + 1].level) : null;
        const isLast = index === path.length - 1;
        return (
          <div key={item.id} className="relative">
            <div className="flex items-center gap-3">
              <div className="relative flex flex-col items-center" style={{ width: '32px' }}>
                {index > 0 && (
                  <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 w-0.5" style={{ height: '24px', backgroundColor: colors.line }} />
                )}
                <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${colors.bg} border-2 ${colors.border}`}>
                  {getLevelIcon(item.level, colors.icon)}
                </div>
                {!isLast && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-0.5" style={{ height: '24px', backgroundColor: nextColors?.line || colors.line }} />
                )}
              </div>
              <div className={`flex-1 flex items-center justify-between py-2.5 px-4 rounded-lg border-2 ${isLast ? `${colors.bg} ${colors.border} shadow-sm` : 'bg-white border-slate-200'}`}>
                <span className={`text-sm font-medium ${isLast ? 'text-slate-900 font-semibold' : 'text-slate-700'}`}>{item.name}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${colors.bg} ${colors.border} ${colors.icon}`}>{item.level}</span>
              </div>
            </div>
            {!isLast && <div className="h-4" />}
          </div>
        );
      })}
    </div>
  );
};

export default OrgHierarchyPathDisplay;
